import { useMemo } from 'react';
import type { Area, Issue } from '../../types';
import { displayName, type Profiles } from '../../people';
import VendorTag from '../VendorTag';
import { rollUpWorkspaceAreas, type WorkspaceArea, type AreaBoardMeta } from '../../area-rollup';
import type { EpicHealth } from '../../epic-rollup';
import type { PortfolioRow } from './types';
import { steadyNow } from '../../steady-now';
import { IssueTextInline } from '../IssueText';

/**
 * THE PORTFOLIO'S BY-AREA LENS — every product area across the WHOLE workspace,
 * ranked by open demand (P2, areas-as-object). The sibling of the by-epic lens:
 * that one asks "how is each initiative moving"; this one asks "where is the work
 * going, and who carries it". An area whose cards ride on three boards is one row
 * here, rolled up across all three.
 *
 * ALL DERIVED, and ORGANISING — not measuring. Lead, health, progress and the
 * open/total counts are read from the member cards (the SAME rollUpEpic the epics
 * use, in src/area-rollup.ts); the lens NAMES a lead and a health so an at-risk
 * area has someone to chase, and draws no trend chart — that boundary belongs to
 * Insights. Nothing is typed.
 *
 * There are no lifecycle bands here: an area is not a thing that ships and then
 * runs, the way an epic is, so it is a flat ranked list, not a banded one.
 */

const HEALTH_WORD: Record<EpicHealth, string> = {
  ontrack: 'On track',
  atrisk: 'At risk',
  offtrack: 'Off track',
};
const HEALTH_CLS: Record<EpicHealth, string> = { ontrack: 'g', atrisk: 'a', offtrack: 'r' };

interface Props {
  rows: PortfolioRow[];
  cardsByBoard: Map<string, Issue[]>;
  areasByBoard: Map<string, Area[]>;
  profiles: Profiles;
  loading: boolean;
  error: string | null | undefined;
  onRetry: () => void;
  onOpenArea: (name: string) => void;
}

export default function AreasLens({ rows, cardsByBoard, areasByBoard, profiles, loading, error, onRetry, onOpenArea }: Props) {
  // HELD STILL WITHIN THE MINUTE, so the memos below that take `now` as a
  // dependency actually cache. `Date.now()` here was new on every render, so
  // every roll-up over every card re-ran every time. See src/steady-now.ts.
  const now = steadyNow();

  const boardMeta = useMemo<AreaBoardMeta[]>(
    () => rows.map((r) => ({ id: r.id, name: r.name })),
    [rows],
  );

  const ws = useMemo(
    () => rollUpWorkspaceAreas(cardsByBoard, areasByBoard, boardMeta, now),
    [cardsByBoard, areasByBoard, boardMeta, now],
  );

  if (error) {
    return (
      <div className="pf-board" style={{ padding: '16px 14px' }}>
        <div style={{ fontSize: 13, color: 'var(--t2)', marginBottom: 12 }}>
          The portfolio could not be read. {error}
        </div>
        <button className="btn" onClick={onRetry}>Try again</button>
      </div>
    );
  }

  const areaWord = ws.areas.length === 1 ? 'area' : 'areas';

  const renderRow = (a: WorkspaceArea) => {
    const onBoards = a.boards.length === 1 ? a.boards[0].name : `${a.boards.length} boards`;
    const boardsTitle = a.boards.map((b) => `${b.name} (${b.cards})`).join(', ');
    return (
      <tr key={a.name} className="epl-row epl-row-click" onClick={() => onOpenArea(a.name)} title={`Open ${a.name}`}>
        <td>
          <div className="epics-name">
            <span className="epic-sw" style={{ background: a.colour || 'var(--t3)' }} aria-hidden="true" />
            <span className="nm">{a.name}</span>
          </div>
        </td>
        <td>
          <div className="pf-owner">
            {a.lead ? displayName(a.lead, profiles) : '—'}
            {a.lead && <VendorTag who={a.lead} profiles={profiles} compact />}
            {a.lead && !a.leadStored && <span className="epd-auto">auto</span>}
            {a.leadStale && <span className="area-lead-stale" title="The area lead holds no open card here">no open cards</span>}
          </div>
        </td>
        <td>
          <div className="rag-cell">
            <span className={`rag ${HEALTH_CLS[a.health]}`}><span className="dot" />{HEALTH_WORD[a.health]}</span>
            {a.health !== 'ontrack' && a.healthReason ? <span className="rag-why"><IssueTextInline text={a.healthReason} /></span> : null}
          </div>
        </td>
        <td>
          <div className="pf-pct">
            <span className="fig">{a.progressPct}%</span>
            <div className="bar"><div className="fill" style={{ width: `${a.progressPct}%` }} /></div>
          </div>
        </td>
        <td className="ms">
          <span>{a.open}</span>
          <div className="epics-due-sub">of {a.total}</div>
        </td>
        <td>
          <span className="epl-boards" title={boardsTitle}>{onBoards}</span>
        </td>
      </tr>
    );
  };

  return (
    <>
      <div className="pf-head">
        <div className="pf-head-l">
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--t1)' }}>Areas</div>
          <div className="pf-sub">
            {loading && ws.areas.length === 0 ? '…' : ws.areas.length} {areaWord} · ranked by open work
          </div>
        </div>
      </div>

      <div className="pf-board">
        <table className="pf-table epl-table">
          <thead>
            <tr>
              <th style={{ width: '28%' }}>Area</th>
              <th>Lead</th>
              <th>Health</th>
              <th style={{ width: '16%' }}>Progress</th>
              <th>Open</th>
              <th>On boards</th>
            </tr>
          </thead>
          <tbody>
            {ws.areas.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="pf-owner" style={{ color: 'var(--t3)' }}>
                    {loading
                      ? 'Reading the workspace…'
                      : 'No areas across the workspace yet — set up areas on a board and give its cards one.'}
                  </div>
                </td>
              </tr>
            ) : (
              ws.areas.map(renderRow)
            )}
          </tbody>
        </table>
      </div>

      <p className="hint epics-foot">
        Every product area across the workspace, ranked by how much open work sits in it. Health and progress
        are read from the member cards, and a lead is whoever is set on the area — or its busiest assignee when
        none is; an area on more than one board is rolled up across them all.
        {ws.noArea.total > 0 ? (
          <> {ws.noArea.total} {ws.noArea.total === 1 ? 'card' : 'cards'} carry no area.</>
        ) : null}
      </p>
    </>
  );
}
